import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import UIkit from "uikit";

import {
  badgeStatusMeta,
  getVerificationTooltip,
  getVerificationTooltipAttr,
  normalizeStatus,
} from "./identityVerificationStatus";
import IdentityVerificationModal from "./IdentityVerificationModal";
import {
  clearDecisionReconciliation,
  getDecisionReconciliationStatus,
  isDecisionReconciliationResolved,
  readDecisionReconciliation,
} from "./identityVerificationReconciliation";

const IdentityVerificationBadge = ({
  reservation = {},
  status,
  enabled = false,
  className = "",
}) => {
  const badgeRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshedStatus, setRefreshedStatus] = useState(null);

  const reservationStatus = useMemo(() => {
    const raw =
      status ||
      reservation?.verification_status ||
      reservation?.identity_verification?.status ||
      (reservation?.identity_verification_required ? "required" : "not_required");

    return normalizeStatus(raw);
  }, [reservation, status]);

  useEffect(() => {
    const reconciliation = readDecisionReconciliation(
      reservation?.guest_reservation_id,
    );
    if (!reconciliation) {
      setRefreshedStatus(null);
      return;
    }
    if (isDecisionReconciliationResolved(reconciliation, reservationStatus)) {
      clearDecisionReconciliation(reservation?.guest_reservation_id);
      setRefreshedStatus(null);
      return;
    }
    setRefreshedStatus(getDecisionReconciliationStatus(reconciliation));
  }, [reservation?.guest_reservation_id, reservationStatus]);

  const value = refreshedStatus || reservationStatus;

  const meta = badgeStatusMeta[value] || badgeStatusMeta.not_required;
  const tooltip = useMemo(() => getVerificationTooltip(value), [value]);
  const tooltipAttr = useMemo(() => getVerificationTooltipAttr(value), [value]);
  const toneClass =
    meta.tone === "success"
      ? "identity-verification-tag--success"
      : meta.tone === "warning"
        ? "identity-verification-tag--warning"
        : meta.tone === "danger"
          ? "cancelled"
          : null;
  useEffect(() => {
    if (badgeRef.current) {
      UIkit.update(badgeRef.current);
    }
  }, [tooltipAttr, meta.label]);

  const canReview = Boolean(
    reservation?.guest_reservation_id &&
    !["not_required", "required"].includes(value),
  );
  const handleModalClose = useCallback(() => setModalOpen(false), []);
  const handleStatusChange = useCallback((nextStatus) => {
    if (nextStatus) setRefreshedStatus(normalizeStatus(nextStatus));
  }, []);

  if (enabled !== true) return null;

  const content = meta.label;

  return (
    <>
      {canReview ? (
        <button
          ref={badgeRef}
          type="button"
          className={clsx(
            "hms-booking-tag",
            "identity-verification-tag--button",
            toneClass,
            className,
          )}
          title={tooltip}
          uk-tooltip={tooltipAttr}
          onClick={() => setModalOpen(true)}
        >
          {content}
        </button>
      ) : (
        <div
          ref={badgeRef}
          className={clsx("hms-booking-tag", toneClass, className)}
          title={tooltip}
          uk-tooltip={tooltipAttr}
        >
          {content}
        </div>
      )}
      {canReview && (
        <IdentityVerificationModal
          open={modalOpen}
          reservation={reservation}
          status={value}
          onClose={handleModalClose}
          onStatusChange={handleStatusChange}
        />
      )}
    </>
  );
};

export default IdentityVerificationBadge;
